import { useEffect, useState } from 'react'
import { client, newIdempotencyKey } from '../lib/api'
import type { Entitlements, WalletSnapshot } from '../../shared/types'

type WalletData = WalletSnapshot & {
  subscription?: { code: string; name: string; status: string; periodEnd: string | null }
  adsEnabled?: boolean
  entitlements?: Entitlements
  planCode?: string
}

type LedgerRow = {
  id: string
  type: string
  memo: string | null
  created_at: string
  direction: string
  amount: number
}

const CREDIT_PACKS = [50, 100, 250, 500] as const

export function Wallet({ isDev, notify }: { isDev: boolean; notify: (msg: string) => void }) {
  const [data, setData] = useState<WalletData | null>(null)
  const [transactions, setTransactions] = useState<LedgerRow[] | null>(null)
  const [buying, setBuying] = useState(false)

  const load = () =>
    Promise.all([
      client.wallet().then((d) => setData(d as WalletData)),
      client.walletTransactions().then((r) => setTransactions(r.transactions)),
    ]).catch((e) => notify((e as Error).message))

  useEffect(() => {
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!data) {
    return (
      <div className="loading-center">
        <div className="spinner" />
      </div>
    )
  }

  const isPlus = data.subscription && data.subscription.code !== 'free'
  const demoBilling = data.demoMode
  const ent = data.entitlements

  const buy = async (credits: number) => {
    setBuying(true)
    try {
      const res = await client.buyCredits(credits, newIdempotencyKey('credits'))
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl
        return
      }
      await load()
      notify(res.message || `${credits} credits added`)
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBuying(false)
    }
  }

  const subscribe = async () => {
    setBuying(true)
    try {
      const res = await client.subscribePlus(newIdempotencyKey('plus'))
      if (res.checkoutUrl) {
        window.location.href = res.checkoutUrl
        return
      }
      await load()
      notify(res.message)
    } catch (e) {
      notify((e as Error).message)
    } finally {
      setBuying(false)
    }
  }

  const openPortal = async () => {
    try {
      const res = await client.billingPortal()
      if (res.url) window.location.href = res.url
      else notify(res.message)
    } catch (e) {
      notify((e as Error).message)
    }
  }

  return (
    <div className="panel-page wallet-layout">
      <span className="eyebrow">Billing</span>
      <h1>Credits &amp; Plus</h1>
      {(isDev || demoBilling) && <span className="dev-badge">{demoBilling ? 'Demo billing' : 'DEV'}</span>}

      <div className="balance-hero">
        <div className="bal-label">Available credits</div>
        <div className="bal-value">
          {data.platformCredits.toLocaleString()} <small>credits</small>
        </div>
      </div>

      <div className="credit-packs">
        {CREDIT_PACKS.map((pack) => (
          <button key={pack} className="button secondary credit-pack-btn" onClick={() => buy(pack)} disabled={buying}>
            {buying ? '…' : demoBilling ? `+${pack} (demo)` : `+${pack} credits`}
          </button>
        ))}
      </div>
      {demoBilling && (
        <p className="page-sub">No charge yet — Stripe live checkout activates once keys are configured.</p>
      )}

      <div className="settings-card" style={{ marginTop: 20 }}>
        <div style={{ padding: '16px 18px' }}>
          <small>Plan</small>
          <strong style={{ display: 'block', marginTop: 4 }}>
            {data.subscription?.name ?? 'Free'}
            {data.subscription?.status && data.subscription.status !== 'active'
              ? ` · ${data.subscription.status}`
              : ''}
          </strong>
          <p className="page-sub" style={{ marginTop: 8 }}>
            {isPlus
              ? 'Ad-free discovery and advanced filters. Mutual matches still message free.'
              : 'Free includes discovery and chat. Ads appear roughly every 15–25 profile decisions.'}
          </p>
          <div className="modal-actions" style={{ marginTop: 12 }}>
            {!isPlus && (
              <button className="button primary" onClick={subscribe} disabled={buying}>
                {demoBilling ? 'Upgrade to Plus (demo)' : 'Upgrade to Plus — ad-free'}
              </button>
            )}
            {isPlus && (
              <button className="button secondary" onClick={openPortal} disabled={demoBilling}>
                {demoBilling ? 'Plus active (demo)' : 'Manage billing'}
              </button>
            )}
          </div>
        </div>
      </div>

      {ent && (
        <div className="settings-card" style={{ marginTop: 16 }}>
          <div style={{ padding: '16px 18px' }}>
            <small>Your benefits</small>
            <ul className="entitlements-list">
              {!ent.ads && <li>Ad-free discovery</li>}
              {ent.advancedFilters && <li>Advanced filters</li>}
              {ent.readReceipts && <li>Read receipts</li>}
              {ent.incognito && <li>Incognito browsing</li>}
              {ent.priorityDiscovery && <li>Priority discovery</li>}
              {ent.monthlyCredits > 0 && <li>{ent.monthlyCredits} monthly credits</li>}
              {ent.boosts > 0 && <li>{ent.boosts} profile boosts</li>}
              {ent.lowerFees && <li>Lower introduction fees</li>}
            </ul>
          </div>
        </div>
      )}

      {transactions && transactions.length > 0 && (
        <div className="settings-card" style={{ marginTop: 16 }}>
          <div style={{ padding: '16px 18px' }}>
            <small>Recent activity</small>
            <ul className="transaction-list">
              {transactions.map((tx) => (
                <li key={tx.id}>
                  <span>
                    <strong>{tx.memo || tx.type.replace(/_/g, ' ')}</strong>
                    <small>{new Date(tx.created_at).toLocaleDateString()}</small>
                  </span>
                  <em className={tx.direction === 'credit' ? 'credit-in' : 'credit-out'}>
                    {tx.direction === 'credit' ? '+' : '−'}
                    {tx.amount}
                  </em>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <p className="page-sub">
        Credits are only for optional unmatched introductions. Mutual matches always message free. Same prices for every
        gender.
      </p>
    </div>
  )
}
